import React from "react";
import { Link } from "react-router-dom";
import { ChevronRight, Star, MapPin, Phone, Clock, Utensils, Leaf, Flame } from "lucide-react";
import Reveal from "@/components/Reveal";
import { BookButton, OrderButton } from "@/components/ActionButtons";
import OurServices from "@/components/OurServices";
import { IMG, RESTAURANT } from "@/data/restaurant";
import { MENU } from "@/data/menu";
import useReviews from "@/hooks/useReviews";

const FEATURED_NAMES = [
  "Infuse Lamb Chops",
  "Choori Chaat",
  "Amritsari Lamb Bhuna",
  "Infuse Dal Makhani",
];

function pickFeatured() {
  const all = Object.values(MENU).flat();
  return FEATURED_NAMES.map((n) => all.find((d) => d.name === n)).filter(Boolean);
}

export default function Home() {
  const reviews = useReviews();
  const featured = pickFeatured();

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section data-testid="hero-section" className="relative min-h-[100vh] flex items-end overflow-hidden grain">
        <div
          className="absolute inset-0 kenburns"
          style={{
            backgroundImage: `linear-gradient(180deg, rgba(6,30,25,0.35) 0%, rgba(6,30,25,0.55) 60%, rgba(6,30,25,0.85) 100%), url(${IMG.hero})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pb-24 pt-44 w-full">
          <div className="max-w-3xl">
            <span className="gold-divider font-eyebrow text-[var(--infuse-gold-soft)]">
              Modern Indian Restaurant · St Albans
            </span>
            <h1 className="font-display text-white mt-6 leading-[0.95] text-[clamp(3.2rem,8vw,6.5rem)]">
              Elevated Flavours.<br />
              <em className="not-italic text-[var(--infuse-gold-soft)]">Modern Restaurant</em> Soul.
            </h1>
            <p className="mt-7 text-white/80 text-lg md:text-xl max-w-xl leading-relaxed">
              Celebrated as the premier contemporary Indian dining experience in Hertfordshire — a mesmerising blend of heritage favourites and innovative new flavours.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <BookButton testid="hero-book-cta">Book a Table</BookButton>
              <OrderButton testid="hero-order-cta">Order Online</OrderButton>
              <Link to="/menu" data-testid="hero-menu-cta" className="font-eyebrow text-[0.72rem] text-white/85 hover:text-[var(--infuse-gold-soft)] transition-colors flex items-center gap-2 px-2 self-center">
                Explore the Menu <ChevronRight size={14} />
              </Link>
            </div>
            <div className="mt-12 flex items-center gap-6 text-white/70 text-sm">
              <span className="flex items-center gap-1.5">
                {[1,2,3,4,5].map(i => <Star key={i} size={14} className="fill-[var(--infuse-gold-soft)] text-[var(--infuse-gold-soft)]" />)}
              </span>
              <span className="font-eyebrow text-[0.65rem] text-white/70">4.6 · 600+ Reviews</span>
            </div>
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <section className="bg-[var(--infuse-emerald)] border-y border-[var(--infuse-gold)]/30 overflow-hidden">
        <div className="flex marquee-track whitespace-nowrap py-6">
          {[...Array(2)].map((_, k) => (
            <div key={k} className="flex items-center gap-16 pr-16 font-display text-2xl md:text-3xl text-[var(--infuse-gold-soft)]">
              <span>Infuse Lamb Chops</span><span className="text-white/30">✦</span>
              <span>Choori Chaat</span><span className="text-white/30">✦</span>
              <span>Chicken Biryani Zaffrani</span><span className="text-white/30">✦</span>
              <span>Infuse Dal Makhani</span><span className="text-white/30">✦</span>
              <span>Truffle Naan</span><span className="text-white/30">✦</span>
              <span>Saffron Negroni</span><span className="text-white/30">✦</span>
            </div>
          ))}
        </div>
      </section>

      {/* OUR STORY */}
      <section data-testid="story-section" className="relative py-24 md:py-32 bg-[var(--infuse-cream)]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid md:grid-cols-12 gap-12 items-center">
          <Reveal className="md:col-span-6">
            <div className="relative">
              <img src={IMG.interior1} alt="Infuse interior" className="w-full h-[520px] object-cover" data-testid="story-image" />
              <div className="absolute -bottom-6 -right-6 w-44 h-44 border border-[var(--infuse-gold)]/40 hidden md:block" />
            </div>
          </Reveal>
          <Reveal className="md:col-span-6" delay={120}>
            <span className="gold-divider font-eyebrow text-[var(--infuse-gold)]">Our Philosophy</span>
            <h2 className="font-display text-5xl md:text-6xl mt-5 text-[var(--infuse-emerald)] leading-[1]">
              An infusion of heritage <em className="not-italic text-[var(--infuse-pomegranate)]">&amp;</em> imagination.
            </h2>
            <p className="mt-7 text-[var(--infuse-ink)]/75 text-lg leading-relaxed">
              Infuse takes traditional Indian heritage recipes and elevates them with the relaxed soul of a contemporary restaurant. Half of our menu is dedicated to meticulously crafted vegetarian and vegan dishes — alongside a curated, inclusive drinks list featuring signature craft cocktails and our beloved <em className="text-[var(--infuse-emerald)] font-medium">"I Am Driving"</em> mocktails.
            </p>
            <div className="mt-10 grid grid-cols-3 gap-6">
              {[
                { icon: Utensils, label: "Chef-led", sub: "Modern restaurant" },
                { icon: Leaf, label: "50% plant", sub: "Vegan & veg" },
                { icon: Flame, label: "Live tandoor", sub: "Clay-oven craft" },
              ].map(({ icon: Icon, label, sub }) => (
                <div key={label} className="border-t border-[var(--infuse-gold)]/40 pt-4">
                  <Icon size={20} strokeWidth={1.5} className="text-[var(--infuse-pomegranate)]" />
                  <p className="mt-2 font-display text-xl text-[var(--infuse-emerald)]">{label}</p>
                  <p className="font-eyebrow text-[0.62rem] text-[var(--infuse-ink)]/60 mt-1">{sub}</p>
                </div>
              ))}
            </div>
            <Link to="/story" data-testid="story-read-more" className="inline-flex items-center gap-2 mt-10 font-eyebrow text-[var(--infuse-pomegranate)] hover:gap-3 transition-all">
              Read the full story <ChevronRight size={14} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* OUR SERVICES */}
      <OurServices />

      {/* FEATURED MENU */}
      <section data-testid="featured-menu" className="relative py-24 md:py-32 bg-[var(--infuse-emerald)] text-white overflow-hidden">
        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: `url(${IMG.emeraldBg})`, backgroundSize: "cover" }} />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
          <Reveal>
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
              <div>
                <span className="gold-divider font-eyebrow text-[var(--infuse-gold-soft)]">Signature Dishes</span>
                <h2 className="font-display text-5xl md:text-6xl mt-5 max-w-2xl leading-[1]">
                  Plates that define <em className="not-italic text-[var(--infuse-gold-soft)]">Infuse.</em>
                </h2>
              </div>
              <Link to="/menu" data-testid="featured-view-menu" className="btn-ghost-gold self-start">View Full Menu</Link>
            </div>
          </Reveal>

          <div className="mt-16 grid md:grid-cols-12 gap-6">
            {featured.map((dish, idx) => (
              <Reveal
                key={dish.name}
                className={`group relative overflow-hidden border border-white/10 bg-white/5 backdrop-blur-sm ${
                  idx === 0 ? "md:col-span-7 md:row-span-2" : idx === 1 ? "md:col-span-5" : "md:col-span-6"
                }`}
                delay={idx * 80}
              >
                <div className="relative h-[280px] md:h-full">
                  {dish.img ? (
                    <img src={dish.img} alt={dish.name} className="absolute inset-0 w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105" />
                  ) : (
                    <div className="absolute inset-0 bg-gradient-to-br from-emerald-900 to-emerald-950" />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-emerald-950/95 via-emerald-950/40 to-transparent" />
                  <div className="relative h-full flex flex-col justify-end p-7 md:p-9">
                    {dish.tags?.[0] && (
                      <span className="font-eyebrow text-[0.6rem] text-[var(--infuse-gold-soft)] mb-3">{dish.tags[0]}</span>
                    )}
                    <h3 className="font-display text-3xl md:text-4xl text-white">{dish.name}</h3>
                    <p className="mt-3 text-white/75 text-sm md:text-base max-w-md leading-relaxed">{dish.desc}</p>
                    <span className="mt-4 font-eyebrow text-[var(--infuse-gold-soft)]">{dish.price}</span>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* REVIEWS */}
      <section data-testid="reviews-section" className="py-24 md:py-32 bg-[var(--infuse-cream)]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <Reveal>
            <span className="gold-divider font-eyebrow text-[var(--infuse-gold)]">Guest Praise</span>
            <h2 className="font-display text-5xl md:text-6xl mt-5 text-[var(--infuse-emerald)] max-w-3xl leading-[1]">
              "A local gem with a brilliant atmosphere."
            </h2>
          </Reveal>

          <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {reviews.slice(0, 6).map((r, i) => (
              <Reveal key={r.id} delay={i * 60} className="bg-white p-8 border border-[var(--infuse-gold)]/20 hover:border-[var(--infuse-gold)]/60 transition-colors duration-500">
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(r.rating)].map((_, k) => (
                    <Star key={k} size={14} className="fill-[var(--infuse-gold)] text-[var(--infuse-gold)]" />
                  ))}
                </div>
                <p className="font-display text-2xl text-[var(--infuse-ink)] leading-snug">"{r.text}"</p>
                <div className="mt-6 flex items-center justify-between text-sm">
                  <span className="font-medium text-[var(--infuse-emerald)]">{r.author}</span>
                  <span className="font-eyebrow text-[0.62rem] text-[var(--infuse-ink)]/50">via {r.source}</span>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal className="mt-14 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 border-t border-[var(--infuse-gold)]/30 pt-10">
            <div>
              <p className="font-eyebrow text-[0.7rem] text-[var(--infuse-gold)]">Dined with us?</p>
              <p className="font-display text-3xl md:text-4xl text-[var(--infuse-emerald)] mt-2 leading-tight">
                We'd love to hear your story.
              </p>
            </div>
            <a
              href="https://g.page/r/CZZViSPMgP9gEBM/review"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="feedback-cta"
              className="btn-primary"
            >
              Give Us Feedback <Star size={14} className="fill-white" strokeWidth={1.5} />
            </a>
          </Reveal>
        </div>
      </section>

      {/* LOCATION / CTA */}
      <section data-testid="location-section" className="relative bg-[var(--infuse-ink)] text-white">
        <div className="grid md:grid-cols-2">
          <div className="p-10 md:p-20">
            <span className="gold-divider font-eyebrow text-[var(--infuse-gold-soft)]">Find Us</span>
            <h2 className="font-display text-5xl md:text-6xl mt-5 leading-[1]">
              A hidden gem, just off St Peter's Street.
            </h2>
            <div className="mt-10 space-y-5 text-white/80">
              <p className="flex items-start gap-3"><MapPin size={18} className="mt-0.5 text-[var(--infuse-gold-soft)]" /> {RESTAURANT.address}</p>
              <p className="flex items-center gap-3"><Phone size={18} className="text-[var(--infuse-gold-soft)]" /> <a href={RESTAURANT.phoneHref} className="hover:text-[var(--infuse-gold-soft)]">{RESTAURANT.phone}</a></p>
              <div className="flex items-start gap-3">
                <Clock size={18} className="mt-1 text-[var(--infuse-gold-soft)]" />
                <div className="grid grid-cols-[120px_1fr] gap-y-1.5 text-sm">
                  {RESTAURANT.hours.map((h) => (
                    <React.Fragment key={h.day}>
                      <span className="font-eyebrow text-[0.62rem] text-white/60">{h.day}</span>
                      <span className="text-white/85">{h.time}</span>
                    </React.Fragment>
                  ))}
                </div>
              </div>
            </div>
            <div className="mt-10 flex flex-wrap gap-3">
              <BookButton testid="location-book-cta">Book a Table</BookButton>
              <OrderButton testid="location-order-cta">Order Now</OrderButton>
              <a href={RESTAURANT.mapsLink} target="_blank" rel="noreferrer" className="font-eyebrow text-[0.72rem] text-white/85 hover:text-[var(--infuse-gold-soft)] flex items-center gap-2 px-2 self-center">
                Get Directions <ChevronRight size={14} />
              </a>
            </div>
          </div>
          <div className="relative min-h-[420px] md:min-h-full">
            <iframe
              title="Infuse Restaurant Map"
              src={RESTAURANT.mapsEmbed}
              className="absolute inset-0 w-full h-full grayscale-[0.4]"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      </section>
    </div>
  );
}
